import { useSearchUnassignedRCs } from '@modules/common/hooks';
import { mapEdgeCategoryToCategory } from '@modules/common/utils';
import { stopPropagationForHotKeysFromInput } from '@modules/taxonomy/helpers/stopEventPropagationForHotkeysFromInput';
import { TaxonomyActionType } from '@modules/taxonomy/store/taxonomyActionType';
import { useTaxonomyStore } from '@modules/taxonomy/store/taxonomyStore';
import { ReasonDefinition } from '@modules/taxonomy/store/types';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Box, List, Stack, TextField, Typography } from '@mui/material';
import { shallow } from 'zustand/shallow';
import { CircularLoader } from '../CircularLoader';
import { ReasonDefinitionInfoTooltip } from '../ReasonDefinitionInfoTooltip';
import { EmptyReasonDefinitionContainer } from './EmptyReasonDefinitionContainer';
import {
  getSelectedUnassignedDefinitions,
  trackCreateNewReasonEvents,
} from './helpers';
import { ReasonDefinitionListItem } from './ReasonDefinitionListItem';

export const SuggestedReasonDefinitionList = () => {
  const dispatch = useTaxonomyStore(({ dispatch }) => dispatch);
  const { reasonDefinitionSearchTerm, reasonDefinitions } = useTaxonomyStore(
    ({ createReasonV3State: { reasonDefinitions, misc } }) => ({
      reasonDefinitions,
      reasonDefinitionSearchTerm: misc?.reasonDefinitionSearchTerm,
    }),
    shallow,
  );

  const {
    data: searchUnassignedRCsResponse,
    loading: areUnassignedRCsLoading,
  } = useSearchUnassignedRCs({
    searchTerm: reasonDefinitionSearchTerm?.trim() ?? '',
  });

  const suggestedReasonDefinitions = reasonDefinitions?.filter(
    (definition) =>
      definition.__typename === 'UnassignedRC' && !definition.isSelected,
  );

  const selectedUnassignedRCs = getSelectedUnassignedDefinitions(
    reasonDefinitions,
  ).map((definition) => definition.name);

  const onAddDefinition = (definition: ReasonDefinition) => {
    if (definition.__typename === 'SearchedUnassignedRc') {
      const updatedReasonDefinitions = [
        ...(reasonDefinitions ?? []),
        {
          ...definition,
          isSelected: true,
        },
      ];

      dispatch({
        type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
        payload: {
          reasonDefinitions: updatedReasonDefinitions,
        },
      });

      trackCreateNewReasonEvents({
        eventName: 'Create New Reason - Reason Definition Added',
        reasonDefinition: {
          name: definition.name,
          type: 'SearchedUnassignedRc',
          category: mapEdgeCategoryToCategory(definition.category),
        },
      });
      return;
    }

    const updatedReasonDefinitions = reasonDefinitions?.map(
      (reasonDefinition) => {
        if (reasonDefinition.name === definition.name) {
          return {
            ...definition,
            isSelected: true,
          };
        }
        return reasonDefinition;
      },
    );

    dispatch({
      type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
      payload: {
        reasonDefinitions: updatedReasonDefinitions,
      },
    });

    trackCreateNewReasonEvents({
      eventName: 'Create New Reason - Reason Definition Added',
      reasonDefinition: {
        name: definition.name,
        type: 'UnassignedRC',
        category: mapEdgeCategoryToCategory(definition.category),
      },
    });
  };

  const onSearchTermChange = (searchTerm: string) => {
    dispatch({
      type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
      payload: {
        misc: { reasonDefinitionSearchTerm: searchTerm },
      },
    });
    trackCreateNewReasonEvents({
      eventName: 'Create New Reason - Reason Definition Searched',
      reasonDefinitionFilterTerm: searchTerm,
    });
  };

  const renderReasonDefinitionContainer = (
    reasonDefinition: ReasonDefinition[] | undefined,
  ) => {
    if (!reasonDefinition?.length) {
      return (
        <EmptyReasonDefinitionContainer
          icon={<>📭</>}
          primaryText="No suggestions available at the moment."
          secondaryText="You can try searching or consider editing your prompt."
        />
      );
    }

    return (
      <Box height="100%" overflow="auto">
        <List disablePadding style={{ marginTop: 0, padding: 0 }}>
          {reasonDefinition?.map((definition) => {
            return (
              <ReasonDefinitionListItem
                key={definition.name}
                definition={definition}
                onAddDefinition={() => onAddDefinition(definition)}
              />
            );
          })}
        </List>
      </Box>
    );
  };

  const renderSearchReasonDefinitionContainer = () => {
    const unassignedRCs =
      searchUnassignedRCsResponse?.searchUnassignedRCs?.unassignedRCs;

    const filteredUnassignedRCs = unassignedRCs
      ?.filter((definition) => {
        return !selectedUnassignedRCs?.includes(definition.name);
      })
      .map((definition): ReasonDefinition => {
        return {
          ...definition,
          __typename: 'SearchedUnassignedRc',
        };
      });

    if (areUnassignedRCsLoading) {
      return (
        <CircularLoader sx={{ top: '50%', position: 'absolute', mt: 2 }} />
      );
    }

    if (!filteredUnassignedRCs || !filteredUnassignedRCs.length) {
      return (
        <EmptyReasonDefinitionContainer
          icon={<>🔍</>}
          primaryText="No matching definitions found."
          secondaryText="Try refining your search."
        />
      );
    }

    return renderReasonDefinitionContainer(filteredUnassignedRCs);
  };

  return (
    <Stack pt={0.25} height="100%" position="relative">
      <Stack px={1.25} spacing={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchRoundedIcon color="disabled" />
            <Typography variant="subtitle1">
              Find and add more Definitions
            </Typography>
          </Stack>
          <ReasonDefinitionInfoTooltip />
        </Stack>
        <TextField
          fullWidth
          value={reasonDefinitionSearchTerm}
          size="small"
          onChange={(event) => onSearchTermChange(event.target.value)}
          placeholder="Search Definitions"
          onKeyDown={stopPropagationForHotKeysFromInput}
        />
      </Stack>
      {reasonDefinitionSearchTerm?.length
        ? renderSearchReasonDefinitionContainer()
        : renderReasonDefinitionContainer(suggestedReasonDefinitions)}
    </Stack>
  );
};
